import { BrowserModule } from '@angular/platform-browser';
import { Injector, LOCALE_ID, NgModule, provideAppInitializer, inject } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BidiModule } from '@angular/cdk/bidi';
import { AppComponent } from './components/main/app.component';
import { HttpClient } from '@angular/common/http';
import { ToastrModule } from 'ngx-toastr';
import { LayoutModule } from './modules/layout/layout.module';
import { HomeComponent } from './components/layout/home.component';
import { AppRoutingModule } from './app-routing.module';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import appConfig from '@config';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { DirectivesModule } from '@ui';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '@env';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AppInjector } from '@app/services/app-injector.service';
import { ModulesSharedModule } from '@app/modules/modules-shared.module';
import { OverlayModule } from '@angular/cdk/overlay';
import { FingerPrintService } from '@core/services/finger-print/finger-print.service';
import { NewUiModule } from '@app/shared/ui/new-ui.module';
import { NewCoreModule } from '@app/core/new-core.module';
import { SideBarComponent } from '@app/components/general/side-bar/side-bar.component';
import { NavBarComponent } from '@app/components/general/nav-bar/nav-bar.component';
import { MatBadgeModule } from '@angular/material/badge';
import { provideAppHttpClient } from '@app/core/interceptors/interceptors';
import { appInitializer, msalInitializer } from '@app/core/initializers/app.initializers';
import { registerLocaleData } from '@angular/common';
import myLocaleHe from '@angular/common/locales/he';
import { LoginPageModule } from '@app/pages/login/module/login-page.module';
import { NewSharedModule } from '@app/shared/new-shared.module';
import { InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import {
	MSAL_GUARD_CONFIG,
	MSAL_INSTANCE,
	MSAL_INTERCEPTOR_CONFIG,
	MsalGuard,
	MsalGuardConfiguration,
	MsalInterceptorConfiguration,
	MsalModule,
	MsalService
} from '@azure/msal-angular';
import { ZendeskConfig } from '@app/zendesk/zendesk-config';
import { PendingChangesGuard } from '@app/pages/clients-and-matters/guards/pending-changes.guard';
import { NgxZendeskWebwidgetModule } from '@nitsanzo/ngx-zendesk-webwidget';
import { KanbanModule } from '@syncfusion/ej2-angular-kanban';
import { NotificationService } from '@app/services/notification/notification.service';
import { NewDrawerModule } from '@app/shared/components/new-drawer/module/new-drawer.module';

registerLocaleData(myLocaleHe);

export function createTranslateLoader(http: HttpClient) {
	return new TranslateHttpLoader(http, `./${appConfig.TRANSLATION_PATH}`, '.json');
}

export function MSALInstanceFactory(): IPublicClientApplication {
	return new PublicClientApplication({
		auth: {
			clientId: '', // Application (client) ID from the app registration
			authority: '' // The Azure cloud instance and the app's sign-in audience (tenant ID, common, organizations, or consumers)
		},
		cache: {
			cacheLocation: 'localStorage'
		}
	});
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		authRequest: {
			scopes: ['user.read'] // Define the scopes your application needs
		}
	};
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		protectedResourceMap: new Map([['https://graph.microsoft.com/v1.0/me', ['user.read']]])
	};
}

@NgModule({
	declarations: [AppComponent, HomeComponent, SideBarComponent, NavBarComponent],
	bootstrap: [AppComponent],
	imports: [
		LoginPageModule,
		NgxZendeskWebwidgetModule.forRoot(ZendeskConfig),
		MsalModule,
		BrowserModule,
		BrowserAnimationsModule,
		BidiModule,
		LayoutModule,
		DirectivesModule,
		AppRoutingModule,
		OverlayModule,
		MatCardModule,
		ModulesSharedModule,
		NewUiModule,
		NewCoreModule,
		MatProgressSpinnerModule,
		MatBadgeModule,
		ToastrModule.forRoot(),
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: createTranslateLoader,
				deps: [HttpClient]
			}
		}),
		ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
		NewSharedModule,
		KanbanModule,
		NewDrawerModule
	],
	providers: [
		PendingChangesGuard,
		FingerPrintService,
		provideAppInitializer(msalInitializer),
		provideAppInitializer(appInitializer),
		{ provide: LOCALE_ID, useValue: 'he' },
		{
			provide: MSAL_INSTANCE,
			useFactory: MSALInstanceFactory
		},
		{
			provide: MSAL_GUARD_CONFIG,
			useFactory: MSALGuardConfigFactory
		},
		{
			provide: MSAL_INTERCEPTOR_CONFIG,
			useFactory: MSALInterceptorConfigFactory
		},
		MsalService,
		MsalGuard,
		NotificationService,
		provideAppHttpClient()
	]
})
export class AppModule {
	constructor() {
		const injector = inject(Injector);

		AppInjector.injector = injector;
	}
}
